import { useCallback, useRef } from 'react'

/**
 * Restores Enter-to-activate on a `<Pressable>` that carries an explicit
 * `accessibilityRole`.
 *
 * Measured on react-native-web 0.21 with real CDP key events, A/B'd by adding and
 * removing this hook against the same probe: a focused Pressable with
 * `accessibilityRole="button"` does NOT fire `onPress` on Enter (its
 * PressResponder calls `stopPropagation()` for Enter and never ends the press),
 * while a *roleless* Pressable does. Adding the role is what makes a button
 * announce correctly to a screen reader, so the two cannot be traded off — the
 * role goes on, and Enter is restored here.
 *
 * Enter is the key screen-reader users reach for first, so without this a
 * correctly-labelled button is still unusable to them.
 *
 * Verified not to double-fire: the bottom-bar FAB is a toggle, so a double
 * activation would leave its menu closed. It opens.
 */
const usePressOnEnter = (onPress?: (...args: any[]) => void, disabled?: boolean) => {
  // Guards against a held Enter auto-repeating into many presses. Cleared on the
  // matching keyup, listened for on the document because focus can leave the
  // button (the press may navigate) before the key is released.
  const firedRef = useRef(false)

  const onKeyDown = useCallback(
    (e: any) => {
      if (disabled || !onPress) return

      const key = e?.key ?? e?.nativeEvent?.key
      if (key !== 'Enter') return
      if (e?.repeat || e?.nativeEvent?.repeat) return
      if (firedRef.current) return

      firedRef.current = true
      if (typeof document !== 'undefined') {
        const reset = () => {
          firedRef.current = false
          document.removeEventListener('keyup', reset)
        }
        document.addEventListener('keyup', reset)
      }

      onPress(e)
    },
    [onPress, disabled]
  )

  return { onKeyDown }
}

export default usePressOnEnter
